/**
 * Help Center Articles Index
 * 
 * Exports all articles and helper functions
 */

import type { HelpArticle, HelpCategoryId } from "../types";
import { ACCOUNT_ARTICLES } from "./account";
import { DEPOSITS_ARTICLES } from "./deposits";
import { GETTING_STARTED_ARTICLES } from "./getting-started";
import { LIMITS_FEES_ARTICLES } from "./limits-fees";
import { SECURITY_ARTICLES } from "./security";
import { TRANSACTIONS_ARTICLES } from "./transactions";
import { TROUBLESHOOTING_ARTICLES } from "./troubleshooting";
import { WITHDRAWALS_ARTICLES } from "./withdrawals";

// All articles combined
export const ALL_ARTICLES: HelpArticle[] = [
  ...GETTING_STARTED_ARTICLES,
  ...ACCOUNT_ARTICLES,
  ...DEPOSITS_ARTICLES,
  ...WITHDRAWALS_ARTICLES,
  ...TRANSACTIONS_ARTICLES,
  ...LIMITS_FEES_ARTICLES,
  ...SECURITY_ARTICLES,
  ...TROUBLESHOOTING_ARTICLES,
];

// Export individual category articles
export {
  ACCOUNT_ARTICLES,
  DEPOSITS_ARTICLES, GETTING_STARTED_ARTICLES, LIMITS_FEES_ARTICLES,
  SECURITY_ARTICLES, TRANSACTIONS_ARTICLES, TROUBLESHOOTING_ARTICLES, WITHDRAWALS_ARTICLES
};

/**
 * Get articles by category
 */
export function getArticlesByCategory(categoryId: HelpCategoryId): HelpArticle[] {
  return ALL_ARTICLES
    .filter((article) => article.category === categoryId)
    .sort((a, b) => a.order - b.order);
}

/**
 * Get article by slug
 */
export function getArticleBySlug(slug: string): HelpArticle | undefined {
  return ALL_ARTICLES.find((article) => article.slug === slug);
}

/**
 * Get article by ID
 */
export function getArticleById(id: string): HelpArticle | undefined {
  return ALL_ARTICLES.find((article) => article.id === id);
}

/**
 * Get popular articles
 */
export function getPopularArticles(limit = 6): HelpArticle[] {
  return ALL_ARTICLES
    .filter((article) => article.popular)
    .slice(0, limit);
}

/**
 * Search articles by query
 */
export function searchArticles(query: string): HelpArticle[] {
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) return [];

  return ALL_ARTICLES.filter((article) => {
    const searchableText = [
      article.title,
      article.description,
      ...article.keywords,
      article.content,
    ]
      .join(" ")
      .toLowerCase();

    // Split query into words and check if all are present
    const queryWords = lowerQuery.split(/\s+/);
    return queryWords.every((word) => searchableText.includes(word));
  });
}

/**
 * Get article count by category
 */
export function getArticleCountByCategory(): Record<HelpCategoryId, number> {
  const counts: Record<string, number> = {};

  for (const article of ALL_ARTICLES) {
    counts[article.category] = (counts[article.category] || 0) + 1;
  }

  return counts as Record<HelpCategoryId, number>;
}

/**
 * Get total article count
 */
export function getTotalArticleCount(): number {
  return ALL_ARTICLES.length;
}
